/**
 * Browser media-capture helpers for E2E attachments: read a
 * File to bytes, infer the media type, probe dimensions/duration and record
 * a voice note with MediaRecorder. Everything is encrypted E2E before leaving (see
 * `lib/chat/media.ts`): these helpers only obtain the local PLAINTEXT.
 */

import type { MediaKind } from "@/lib/chat/media";

export function mediaKindFromMime(mime: string): MediaKind {
  if (mime.startsWith("image/")) {
    return "image";
  }
  if (mime.startsWith("video/")) {
    return "video";
  }
  if (mime.startsWith("audio/")) {
    return "voice";
  }
  return "file";
}

export async function readFileBytes(file: Blob): Promise<Uint8Array> {
  return new Uint8Array(await file.arrayBuffer());
}

/** Dimensions of an image (best-effort; does not fail if it does not load). */
export async function probeImageSize(url: string): Promise<{ width?: number; height?: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.addEventListener("load", () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight }),
    );
    img.addEventListener("error", () => resolve({}));
    img.src = url;
  });
}

/** Dimensions + duration of a video (best-effort). */
export async function probeVideoMeta(
  url: string,
): Promise<{ width?: number; height?: number; durationMs?: number }> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.addEventListener("loadedmetadata", () =>
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        durationMs: Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : undefined,
      }),
    );
    video.addEventListener("error", () => resolve({}));
    video.src = url;
  });
}

function pickAudioMime(): string {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
  if (typeof MediaRecorder === "undefined") {
    return "audio/webm";
  }
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) {
      return c;
    }
  }
  return "audio/webm";
}

interface RecordedVoice {
  readonly blob: Blob;
  readonly mime: string;
  readonly durationMs: number;
}

/** Voice-note recorder on top of MediaRecorder (microphone → opus Blob). */
export class VoiceRecorder {
  private rec?: MediaRecorder;
  private stream?: MediaStream;
  private chunks: Blob[] = [];
  private startedAt = 0;

  get recording(): boolean {
    return this.rec?.state === "recording";
  }

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mime = pickAudioMime();
    this.rec = new MediaRecorder(this.stream, { mimeType: mime });
    this.chunks = [];
    this.rec.addEventListener("dataavailable", (e) => {
      if (e.data.size > 0) {
        this.chunks.push(e.data);
      }
    });
    this.rec.start();
    this.startedAt = Date.now();
  }

  async stop(): Promise<RecordedVoice> {
    const { rec } = this;
    if (!rec) {
      throw new Error("voice: no recording in progress");
    }
    return new Promise<RecordedVoice>((resolve) => {
      rec.addEventListener(
        "stop",
        () => {
          const mime = rec.mimeType || "audio/webm";
          const blob = new Blob(this.chunks, { type: mime });
          const durationMs = Date.now() - this.startedAt;
          this.cleanup();
          resolve({ blob, mime, durationMs });
        },
        { once: true },
      );
      rec.stop();
    });
  }

  cancel(): void {
    if (this.rec && this.rec.state !== "inactive") {
      this.rec.stop();
    }
    this.cleanup();
  }

  private cleanup(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = undefined;
    this.rec = undefined;
    this.chunks = [];
  }
}
